import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { useAuthContext } from '../components/Authentication/hooks/useAuthContext'
import { SNIPPET_START_CHAR } from '../constants'
import { addSnippet, deleteSnippet as removeSnippet, setError, setLoading, setSnippets, updateSnippet } from '../redux/reducers/snippets-slice'
import { RootState } from '../redux/store'
import * as snippetsService from '../services/http/snippets.service'
import { Snippet } from '../services/models/Snippet.model'

/**
 * Interface defining the shape of snippet operations and state
 * Provides CRUD operations and state management for snippets
 */
export interface SnippetsHookState {
    snippets: Snippet[]
    isLoading: boolean
    error: string | null
    fetchSnippets: (userId: string) => Promise<void>
    createSnippet: (userId: string, data: { abbreviation: string; content: string }) => Promise<void>
    editSnippet: (id: string, data: { abbreviation: string; content: string }) => Promise<void>
    deleteSnippet: (id: string) => Promise<void>
    applySnippetHandler: (content: string) => string | undefined
    applySnippetInDictation: (content: string) => { abbrLength: number; snippet: string } | undefined
}

// Hook for managing Redux snippets state and operations
export const useSnippets = (): SnippetsHookState => {
    const { user } = useAuthContext().user
    const dispatch = useDispatch()

    // Selectors for accessing Redux state
    const snippets = useSelector((state: RootState) => state.snippets.snippets)
    const isLoading = useSelector((state: RootState) => state.snippets.loading)
    const error = useSelector((state: RootState) => state.snippets.error)

    /**
     * Fetches all snippets for a user
     * Updates Redux store with fetched snippets
     */
    const fetchSnippets = useCallback(
        async (userId: string) => {
            try {
                dispatch(setLoading(true))
                const response = await snippetsService.getSnippets(userId)
                dispatch(setSnippets(response))
                dispatch(setError(null))
            } catch (err) {
                dispatch(setError('Failed to fetch snippets'))
                toast.error('Failed to fetch snippets')
            } finally {
                dispatch(setLoading(false))
            }
        },
        [dispatch]
    )

    /**
     * Creates a new snippet
     */
    const createSnippet = useCallback(
        async (userId: string, data: { abbreviation: string; content: string }) => {
            try {
                dispatch(setLoading(true))
                const newSnippet = await snippetsService.createSnippet(data, userId)
                // Updates Redux store with the new snippet on success
                dispatch(addSnippet(newSnippet))
                toast.success('Successfully created snippet')
            } catch (err) {
                toast.error('Failed to create snippet')
                throw err
            } finally {
                dispatch(setLoading(false))
            }
        },
        [dispatch]
    )

    const editSnippet = useCallback(
        async (id: string, data: { abbreviation: string; content: string }) => {
            try {
                dispatch(setLoading(true))
                await snippetsService.updateSnippet(id, data)
                dispatch(updateSnippet({ id, ...data }))
                toast.success('Successfully updated snippet')
            } catch (err) {
                toast.error('Failed to update snippet')
                throw err
            } finally {
                dispatch(setLoading(false))
            }
        },
        [dispatch]
    )

    const deleteSnippet = useCallback(
        async (id: string) => {
            try {
                dispatch(setLoading(true))
                await snippetsService.deleteSnippet(id)
                dispatch(removeSnippet(id))
                toast.success('Successfully deleted snippet')
            } catch (err) {
                toast.error('Failed to delete snippet')
                throw err
            } finally {
                dispatch(setLoading(false))
            }
        },
        [dispatch]
    )

    const applySnippetHandler = useCallback(
        (content: string) => {
            // Get the last word of the content with a space at the end
            const lastWord = content.match(/\S+\s/g)?.pop()?.toLowerCase() ?? ''

            // If the last word doesn't start with a dot, return
            if (!lastWord || !lastWord.startsWith(SNIPPET_START_CHAR)) return

            // Find the snippet that matches the last word
            const snippet = snippets.find((snippet) => `${SNIPPET_START_CHAR}${snippet.abbreviation} ` === lastWord)

            // If the snippet is found, replace the last word with the snippet content
            if (snippet) {
                return content.replace(lastWord, snippet.content) + ' '
            }

            return
        },
        [snippets]
    )

    const removeHtmlTags = useCallback((content: string) => {
        return content.replace(/<[^>]*>?/g, '')
    }, [])

    const applySnippetInDictation = useCallback(
        (content: string) => {
            // Remove html tags
            const cleanedContent = removeHtmlTags(content)

            // Get the last word of the content with a space or dot at the end
            const lastWord =
                cleanedContent
                    .match(/\S+\s|\S+\./g)
                    ?.pop()
                    ?.toLowerCase() ?? ''

            // If the last word doesn't start with a dot, return
            if (!lastWord || !lastWord.startsWith(SNIPPET_START_CHAR)) return

            // Find the snippet that matches the last word
            const snippet = snippets.find((snippet) => `${SNIPPET_START_CHAR}${snippet.abbreviation} ` === lastWord.replace('.', ''))
            if (!snippet) return

            // Check if the snippet content include /abbreviation in last word
            const lastWordInSnippet = snippet.content.split(' ')?.pop() ?? ''
            let modifiedSnippetContent = snippet.content

            // If the last word in the snippet content includes abbreviation from snippets array, return
            if (lastWordInSnippet.startsWith(SNIPPET_START_CHAR)) {
                if (snippets.some((snippet) => lastWordInSnippet.includes(snippet.abbreviation))) modifiedSnippetContent = snippet.content + ','
            }

            // Apply snippet handler
            return {
                abbrLength: lastWord.length, // +2 for the dot and space
                snippet: modifiedSnippetContent
            }
        },
        [snippets, removeHtmlTags]
    )

    useEffect(() => {
        if (!user?._id) {
            return
        }
        fetchSnippets(user._id)
    }, [fetchSnippets, user?._id])

    return {
        snippets,
        isLoading,
        error,
        fetchSnippets,
        createSnippet,
        editSnippet,
        deleteSnippet,
        applySnippetHandler,
        applySnippetInDictation
    }
}
